import { useState, useEffect } from 'react';
import { Search, Filter, ChevronLeft, ChevronRight, Mail, Plus, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { adminApi } from '@/services/adminApi';
import LoadingSpinner from '@/components/common/LoadingSpinner';

interface Lead {
  leadId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  leadSource: string;
  leadType: string;
  isSubscribed: boolean;
  emailSendCount: number;
  createdAt: string;
}

export function LeadsPage() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLead, setNewLead] = useState({
    email: '',
    firstName: '',
    lastName: '',
    leadType: 'teen',
    notes: '',
  });
  const limit = 20;

  useEffect(() => {
    loadLeads();
  }, [page, sourceFilter, typeFilter]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getLeads({
        leadSource: sourceFilter || undefined,
        leadType: typeFilter || undefined,
        page,
        limit,
      });
      setLeads(data.leads);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLead = async () => {
    try {
      await adminApi.createLead({
        email: newLead.email,
        firstName: newLead.firstName || undefined,
        lastName: newLead.lastName || undefined,
        leadSource: 'manual',
        leadType: newLead.leadType,
        notes: newLead.notes || undefined,
      });
      setShowAddModal(false);
      setNewLead({ email: '', firstName: '', lastName: '', leadType: 'teen', notes: '' });
      loadLeads();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add lead');
    }
  };

  const handleDeleteLead = async (leadId: string) => {
    if (!confirm('Are you sure you want to delete this lead?')) return;

    try {
      await adminApi.deleteLead(leadId);
      loadLeads();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete lead');
    }
  };

  const filteredLeads = leads.filter((lead) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      lead.email.toLowerCase().includes(search) ||
      lead.firstName?.toLowerCase().includes(search) ||
      lead.lastName?.toLowerCase().includes(search)
    );
  });

  const getSourceBadgeVariant = (source: string) => {
    switch (source) {
      case 'simulator':
        return 'default';
      case 'lead_magnet':
        return 'secondary';
      case 'school_prospect':
        return 'success';
      case 'manual':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'teen':
        return 'default';
      case 'school':
        return 'success';
      case 'coop':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mail className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-foreground">Marketing Leads</h1>
            <p className="text-muted-foreground mt-1">
              Manage leads from all sources
            </p>
          </div>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Lead
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={sourceFilter}
                onChange={(e) => {
                  setSourceFilter(e.target.value);
                  setPage(1);
                }}
                className="form-input w-40"
              >
                <option value="">All Sources</option>
                <option value="simulator">Simulator</option>
                <option value="lead_magnet">Lead Magnet</option>
                <option value="school_prospect">School Prospect</option>
                <option value="coop_inquiry">Co-op Inquiry</option>
                <option value="manual">Manual</option>
              </select>
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(1);
                }}
                className="form-input w-40"
              >
                <option value="">All Types</option>
                <option value="teen">Teen</option>
                <option value="school">School</option>
                <option value="coop">Co-op</option>
                <option value="general">General</option>
              </select>
              <Button variant="outline" onClick={() => loadLeads()}>
                <Filter className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Leads ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingSpinner message="Loading leads..." />
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={loadLeads}>Retry</Button>
            </div>
          ) : filteredLeads.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No leads found
            </p>
          ) : (
            <>
              <div className="table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Source</th>
                      <th>Type</th>
                      <th>Subscribed</th>
                      <th>Emails Sent</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.map((lead) => (
                      <tr key={lead.leadId}>
                        <td className="font-medium">{lead.email}</td>
                        <td className="text-muted-foreground">
                          {lead.firstName || lead.lastName
                            ? `${lead.firstName || ''} ${lead.lastName || ''}`.trim()
                            : '-'}
                        </td>
                        <td>
                          <Badge variant={getSourceBadgeVariant(lead.leadSource)}>
                            {lead.leadSource.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td>
                          <Badge variant={getTypeBadgeVariant(lead.leadType)}>
                            {lead.leadType}
                          </Badge>
                        </td>
                        <td>
                          <Badge variant={lead.isSubscribed ? 'success' : 'destructive'}>
                            {lead.isSubscribed ? 'Yes' : 'No'}
                          </Badge>
                        </td>
                        <td>{lead.emailSendCount}</td>
                        <td className="text-muted-foreground">
                          {new Date(lead.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteLead(lead.leadId)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-3 py-1 text-sm">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Lead Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={newLead.email}
                onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={newLead.firstName}
                  onChange={(e) => setNewLead({ ...newLead, firstName: e.target.value })}
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={newLead.lastName}
                  onChange={(e) => setNewLead({ ...newLead, lastName: e.target.value })}
                  placeholder="Doe"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="leadType">Lead Type</Label>
              <select
                id="leadType"
                value={newLead.leadType}
                onChange={(e) => setNewLead({ ...newLead, leadType: e.target.value })}
                className="form-input w-full"
              >
                <option value="teen">Teen</option>
                <option value="school">School</option>
                <option value="coop">Co-op</option>
                <option value="general">General</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                value={newLead.notes}
                onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                className="form-input w-full min-h-[80px]"
                placeholder="Optional notes about this lead..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddLead} disabled={!newLead.email}>
              Add Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
